import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import { ChevronLeftIcon, LightBulbIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import SearchVerses from '../components/SearchVerses';
import { getSuggestedVerses } from '../utils/verseAlgorithm';
import SuggestedVerses from '../components/SuggestedVerses';
import { GROWTH_AREAS } from '../utils/constants';

const SuggestedVersesPage = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [discipleData, setDiscipleData] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const [allVerses, setAllVerses] = useState([]);
  const [notes, setNotes] = useState([]);
  const [historicalVerses, setHistoricalVerses] = useState([]);
  const [sentVerses, setSentVerses] = useState([]);
  const [userAppID, setUserAppID] = useState('');
  const [suggestedVerses, setSuggestedVerses] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [themeFilter, setThemeFilter] = useState('');
  const [showSearchAll, setShowSearchAll] = useState(false);

  const firstName = discipleData?.name?.split(/\s+/)[0] || 'them';

  const sentVerseRefs = useMemo(() => new Set((sentVerses || []).map((v) => v.verseReference)), [sentVerses]);

  // When search or theme is set: filter from ALL verses in the system. Otherwise show top suggestions only.
  const filteredSuggestions = useMemo(() => {
    const key = searchKey.trim().toLowerCase();
    const theme = themeFilter.trim();
    const hasFilter = key.length > 0 || theme.length > 0;

    if (hasFilter) {
      const availableVerses = allVerses.filter((v) => !sentVerseRefs.has(v.reference));
      return availableVerses
        .filter((v) => {
          const ref = (v.reference || '').toLowerCase();
          const text = (v.text || v.passage || '').toLowerCase();
          const matchKey = !key || ref.includes(key) || text.includes(key);
          const verseTheme = (v.growthArea || '').trim().toLowerCase();
          const matchTheme = !theme || verseTheme === theme.toLowerCase();
          return matchKey && matchTheme;
        })
        .map((verse) => ({ verse, contextInfo: {}, explanation: '' }));
    }

    return suggestedVerses;
  }, [suggestedVerses, allVerses, sentVerseRefs, searchKey, themeFilter]);

  // Fetch all necessary data
  useEffect(() => {
    const fetchData = async () => {
      if (!discipleId || !currentUser) return;
      setLoading(true);

      try {
        // Get user's appID
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const appID = userDoc.data()?.appID;
        if (!appID) {
          throw new Error("User appID not found.");
        }
        setUserAppID(appID);

        // Fetch disciple data, all verses, notes, messages, AND mentor details in parallel.
        // Messages live under mentorDetails (per user): messages + messagesSMS.
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
        const [ 
          discipleDoc, 
          mentorDetailsDoc,
          versesSnapshot, 
          notesSnapshot, 
          messagesSnapshot, 
          messagesSMSSnapshot 
        ] = await Promise.all([
          getDoc(doc(db, 'disciples', discipleId)),
          getDoc(mentorDetailsRef),
          getDocs(collection(db, 'verses')),
          getDocs(collection(db, 'disciples', discipleId, 'mentorDetails', appID, 'notes')),
          getDocs(collection(mentorDetailsRef, 'messages')),
          getDocs(collection(mentorDetailsRef, 'messagesSMS'))
        ]);

        // Process disciple data
        if (discipleDoc.exists()) {
          setDiscipleData(discipleDoc.data());
        } else {
          console.warn("Disciple document not found");
        }

        // Process mentor details
        if (mentorDetailsDoc.exists()) {
          setMentorDetails(mentorDetailsDoc.data());
        } else {
          console.warn("Mentor details document not found");
        }

        // Process verses
        const versesData = versesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllVerses(versesData);

        // Process notes
        const notesData = notesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setNotes(notesData);

        // Process sent verses from both email and SMS message subcollections (same path as ViewDisciple / SentVerses)
        const fromDocs = (snap) => snap.docs.map(d => ({ verseReference: d.data().verseReference, sentAt: d.data().sentAt })).filter(v => v.verseReference);
        const sentVersesList = [...fromDocs(messagesSnapshot), ...fromDocs(messagesSMSSnapshot)];
        setSentVerses(sentVersesList);
        setHistoricalVerses(sentVersesList);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [discipleId, currentUser]);

  // Generate suggestions using the same algorithm as ViewDisciple's Suggestions module.
  // Do NOT require notes: when notes are empty, getSuggestedVerses uses balanced suggestions.
  useEffect(() => {
    if (!allVerses?.length || !discipleId) return;

    const mentor = mentorDetails || {};
    const context = {
      ageRange: mentor.faithJourneyAgeRange,
      relationshipStatus: mentor.faithJourneyRelationship,
      discipleshipStage: mentor.faithJourneyStage,
      christianExperience: mentor.faithJourneyExperience
    };

    const filteredNotes = (notes || [])
      .filter(note => !note.archived && note.note !== 'Initial note for disciple')
      .sort((a, b) => {
        const aTime = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt || a.date).getTime();
        const bTime = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt || b.date).getTime();
        return bTime - aTime;
      });
    const notesForAnalysis = filteredNotes.slice(0, 2);

    const sentVerseRefs = new Set((sentVerses || []).map(v => v.verseReference));
    const availableVerses = allVerses.filter(v => !sentVerseRefs.has(v.reference));

    const suggestions = getSuggestedVerses({
      context,
      notes: notesForAnalysis,
      availableVerses,
      count: 7,
      discipleId,
    });
    setSuggestedVerses(suggestions || []);
  }, [mentorDetails, notes, allVerses, sentVerses, discipleId]);

  const handleVerseSelect = async (verse) => {
    try {
      console.log('Selected verse:', verse);
      
      // Navigate to send message with all required data
      navigate(`/send-message`, {
        state: { 
          verse: {
            reference: verse.reference,
            text: verse.text,
            question: verse.reflectionQuestion || verse.question,
            growthArea: verse.growthArea,
            tags: verse.tags
          },
          discipleId: discipleId,
          discipleName: discipleData?.name || '',
          userAppID: userAppID,
        }
      });
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header: back + centered title (TOM mobile style) */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="flex items-center justify-center relative px-4 py-3 min-h-[52px]">
          <button
            onClick={() => navigate(`/view-disciple/${discipleId}/listen-connect-help`, { state: { step: 'help' } })}
            className="absolute left-4 p-1.5 rounded-full hover:bg-gray-100"
            aria-label="Back to Help"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Suggested for {firstName}</h1>
        </div>
      </div>

      {/* Search + Theme row (TOM mobile style) */}
      <div className="bg-white px-4 py-3 border-b border-gray-100">
        <div className="flex gap-2 max-w-2xl mx-auto">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              placeholder="Search verses by key..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] focus:bg-white"
            />
          </div>
          <select
            aria-label="Filter by theme"
            value={themeFilter}
            onChange={(e) => setThemeFilter(e.target.value)}
            className="flex-shrink-0 px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 text-sm focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] focus:bg-white cursor-pointer min-w-[120px]"
          >
            <option value="">All themes</option>
            {GROWTH_AREAS.map((area) => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Info banner (light blue, TOM mobile style) */}
      <div className="px-4 py-3 bg-[#EFF6FF] border-b border-blue-100">
        <div className="flex gap-3 max-w-2xl mx-auto items-start">
          <LightBulbIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            Verses are suggested based on your notes and {firstName}&apos;s faith journey.
          </p>
        </div>
      </div>

      {/* Verse cards (scrollable list) */}
      <div className="px-4 py-4 max-w-2xl mx-auto">
        <SuggestedVerses
          suggestions={filteredSuggestions}
          onVerseSelect={handleVerseSelect}
          sentVerses={sentVerses}
          showHeader={false}
          showTopMatchBadge={!searchKey.trim() && !themeFilter}
        />
        {filteredSuggestions.length === 0 && (
          <p className="text-center text-gray-500 text-sm py-8">
            {searchKey || themeFilter ? 'No verses match your search or theme.' : 'No suggested verses right now. Add notes for more personalized suggestions, or use Search all verses below.'}
          </p>
        )}
      </div>

      {/* Search all verses (expandable) */}
      <div className="px-4 pb-6 max-w-2xl mx-auto">
        <button
          type="button"
          onClick={() => setShowSearchAll((v) => !v)}
          className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-[#0076BE] hover:bg-blue-50 rounded-xl border border-gray-200 bg-white"
        >
          <FunnelIcon className="h-5 w-5" />
          {showSearchAll ? 'Hide search' : 'Search all verses'}
        </button>
        {showSearchAll && (
          <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
            <SearchVerses onVerseSelect={handleVerseSelect} />
          </div>
        )}
      </div>

      {/* Sticky CTA (TOM mobile style) */}
      <div className="fixed bottom-16 left-0 right-0 px-4 z-20 md:bottom-4">
        <div className="max-w-2xl mx-auto">
          <p className="text-center text-sm text-gray-500 py-2">Select a verse to continue</p>
        </div>
      </div>
    </div>
  );
};

export default SuggestedVersesPage; 